<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { lighten, rgba } from 'polished'
import { NButton, NCard, NPopover, NText } from 'naive-ui'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { CreditIcon, SvgIcon } from '@/components/common'
import { useAppStore, useCreditStore } from '@/store'
import { formatMoney } from '@/utils/functions'

interface Props {
  basicCredit: number
  advancedCredit: number
  showCard?: boolean
  collapsed?: boolean
  isFooter?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showCard: false,
  collapsed: false,
  isFooter: false,
})

const appStore = useAppStore()
const creditStore = useCreditStore()
const router = useRouter()
const { t } = useI18n()

const usagePreview = computed(() => creditStore.usagePreview)

const usageLabel = computed(() => {
  const u = usagePreview.value
  if (!u)
    return ''

  const parts: string[] = []

  if (typeof u.total_tokens === 'number')
    parts.push(`${u.total_tokens} tokens`)
  else if (typeof u.prompt_tokens === 'number' || typeof u.completion_tokens === 'number')
    parts.push(`${u.prompt_tokens || 0}/${u.completion_tokens || 0} tokens`)

  if (typeof u.deducted_amount === 'number')
    parts.push(`-${u.deducted_amount}`)

  if (u.credit_type === 'basic_credit')
    parts.push(t('creditDisplay.basic'))
  else if (u.credit_type === 'advance_credit')
    parts.push(t('creditDisplay.advanced'))

  return parts.join(' · ')
})

// Popover state
const showPopover = ref(false)

// Dynamic color system based on primary color
const primaryColor = computed(() => appStore.colorTheme || '#3b82f6')
const primaryColorLight = computed(() => lighten(0.1, primaryColor.value))

// Animated values
const animatedBasicCredit = ref(props.basicCredit)
const animatedAdvancedCredit = ref(props.advancedCredit)

// Animation flags
const basicCreditDecreased = ref(false)
const basicCreditIncreased = ref(false)
const advancedCreditDecreased = ref(false)
const advancedCreditIncreased = ref(false)

// Animation duration
const ANIMATION_DURATION = 1000 // 1 second

const classDiv = computed(() => {
  const className = 'flex items-center rounded-full   hover:scale-105 transition-all duration-200 cursor-pointer ease-in-out'
  if (props.isFooter && props.collapsed)
    return `${className}border flex-col`

  if (props.isFooter)
    return `${className}border`

  return `${className}px-4 py-2`
})

const styleDiv = {
  background: `linear-gradient(to right, ${rgba(primaryColor.value, 0.1)}, ${rgba(primaryColorLight.value, 0.1)})`,
  borderColor: rgba(primaryColor.value, 0.2),
}
const contentClass = computed(() => {
  if (props.showCard && props.collapsed)
    return 'flex flex-col items-center justify-center !px-0'

  if (props.showCard)
    return 'flex items-center justify-center '

  return ''
})

// Function to animate number change
const animateNumber = (currentValue: number, targetValue: number, setter: (value: number) => void, decreaseFlag: any, increaseFlag: any) => {
  const startValue = currentValue
  const difference = targetValue - startValue
  const startTime = Date.now()

  // Set animation flags
  if (difference < 0) {
    decreaseFlag.value = true
    increaseFlag.value = false
  }
  else if (difference > 0) {
    increaseFlag.value = true
    decreaseFlag.value = false
  }
  else {
    decreaseFlag.value = false
    increaseFlag.value = false
  }

  const animate = () => {
    const elapsed = Date.now() - startTime
    const progress = Math.min(elapsed / ANIMATION_DURATION, 1)

    // Easing function for smooth animation
    const easeOutQuart = 1 - (1 - progress) ** 4

    const currentValue = Math.round(startValue + (difference * easeOutQuart))
    setter(currentValue)

    if (progress < 1) {
      requestAnimationFrame(animate)
    }
    else {
      // Reset flags after animation
      setTimeout(() => {
        decreaseFlag.value = false
        increaseFlag.value = false
      }, 300)
    }
  }

  requestAnimationFrame(animate)
}

// Watch basic credit changes
watch(() => props.basicCredit, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    animateNumber(
      animatedBasicCredit.value,
      newValue,
      (value) => { animatedBasicCredit.value = value },
      basicCreditDecreased,
      basicCreditIncreased,
    )
  }
}, { immediate: true })

// Watch advanced credit changes
watch(() => props.advancedCredit, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    animateNumber(
      animatedAdvancedCredit.value,
      newValue,
      (value) => { animatedAdvancedCredit.value = value },
      advancedCreditDecreased,
      advancedCreditIncreased,
    )
  }
}, { immediate: true })

// Handle upgrade click
const handleUpgrade = () => {
  router.push({ name: 'upgrade-pricing' })
}
</script>

<template>
  <NPopover
    v-model:show="showPopover"
    trigger="hover"
    placement="bottom"
    :show-arrow="true"
    :delay="300"
    style="border-radius: 12px;"
  >
    <template #trigger>
      <component
        :is="showCard ? NCard : 'div'"
        :class="showCard ? '!rounded-full shadow !transition-all !duration-200 !ease-in-out' : classDiv"
        :size="showCard ? 'small' : undefined"
        :style="showCard ? '' : isFooter ? '' : styleDiv"
        :content-class="contentClass"
      >
        <!-- Basic Credit Section -->
        <div class="flex items-center w-full justify-center">
          <div class="flex items-center gap-2" :class="(props.showCard || props.isFooter) && props.collapsed ? 'flex-col' : ''">
            <CreditIcon type="basic" :size="14" />
            <span class="ml-0 text-gray-700 dark:text-gray-300 font-semibold text-sm">
              <span
                :key="`basic-${animatedBasicCredit}`"
                class="credit-value"
                :class="{ 'credit-decrease': basicCreditDecreased, 'credit-increase': basicCreditIncreased }"
              >
                {{ formatMoney(animatedBasicCredit) }}
              </span>
            </span>
          </div>
        </div>

        <!-- Divider -->
        <div class="w-px h-4 bg-gray-300 mx-3" :class="(props.showCard || props.isFooter) && props.collapsed ? 'rotate-90' : ''" />

        <!-- Advanced Credit Section -->
        <div class="flex items-center w-full justify-center">
          <div class="flex items-center gap-2" :class="(props.showCard || props.isFooter) && props.collapsed ? 'flex-col' : ''">
            <CreditIcon type="advanced" :size="14" />
            <span class="ml-0 text-purple-600 dark:text-purple-400 font-semibold text-sm">
              <span
                :key="`advanced-${animatedAdvancedCredit}`"
                class="credit-value"
                :class="{ 'credit-decrease': advancedCreditDecreased, 'credit-increase': advancedCreditIncreased }"
              >
                {{ formatMoney(animatedAdvancedCredit) }}
              </span>
            </span>
          </div>
        </div>
      </component>
    </template>

    <!-- Popover Content -->
    <div class="p-4 min-w-[280px]">
      <div v-if="usageLabel" class="mb-3 px-3 py-2 rounded-lg bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-200 text-xs font-semibold">
        {{ usageLabel }}
      </div>
      <!-- Title -->
      <div class="mb-4">
        <NText class="text-xl font-bold text-gray-900 dark:text-white">
          {{ t('creditDisplay.free') }}
        </NText>
      </div>

      <!-- Basic Credit Details -->
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-3">
          <div class="flex items-center gap-2">
            <CreditIcon type="basic" :size="24" />
            <div class="flex flex-col">
              <NText class="text-sm font-semibold text-gray-900 dark:text-white">
                {{ t('creditDisplay.basic') }}
              </NText>
              <NText class="text-xs text-gray-500 dark:text-gray-400">
                {{ t('creditDisplay.basicDescription') }}
              </NText>
            </div>
          </div>
        </div>
        <NText class="text-lg mt-1 font-bold text-gray-900 dark:text-white">
          {{ animatedBasicCredit }}
        </NText>
      </div>

      <!-- Advanced Credit Details -->
      <div class="flex items-center justify-between mb-4">
        <div class="flex items-center gap-3">
          <div class="flex items-center gap-2">
            <CreditIcon type="advanced" :size="24" />
            <div class="flex flex-col">
              <NText class="text-sm font-semibold text-purple-600 dark:text-purple-400">
                {{ t('creditDisplay.advanced') }}
              </NText>
              <NText class="text-xs text-purple-600 dark:text-purple-400">
                {{ t('creditDisplay.advancedDescription') }}
              </NText>
            </div>
          </div>
        </div>
        <NText class="text-lg mt-1 font-bold text-purple-600 dark:text-purple-400">
          {{ animatedAdvancedCredit }}
        </NText>
      </div>

      <!-- Separator -->
      <div class="border-t border-gray-200 dark:border-gray-700 mb-4" />

      <!-- Upgrade Button -->
      <NButton
        type="primary"
        class="w-full"
        size="large"
        :style="{
          background: 'linear-gradient(135deg, #8b5cf6, #a855f7)',
          border: 'none',
          width: '100%',
        }"
        @click="handleUpgrade"
      >
        <template #icon>
          <SvgIcon icon="ri:rocket-line" size="16" />
        </template>
        {{ t('creditDisplay.upgrade') }}
      </NButton>
    </div>
  </NPopover>
</template>

<style scoped>
.credit-value {
  display: inline-block;
  transition: all 0.3s ease;
}

.credit-decrease {
  animation: creditDecrease 0.6s ease-out;
  color: #ef4444 !important; /* Red color for decrease */
}

.credit-increase {
  animation: creditIncrease 0.6s ease-out;
  color: #10b981 !important; /* Green color for increase */
}

@keyframes creditDecrease {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes creditIncrease {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Pulse effect for credit changes */
.credit-decrease::after,
.credit-increase::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  background: currentColor;
  border-radius: 50%;
  transform: translate(-50%, -50%) scale(0);
  opacity: 0.2;
  animation: pulse 0.6s ease-out;
}

@keyframes pulse {
  0% {
    transform: translate(-50%, -50%) scale(0);
    opacity: 0.2;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.5);
    opacity: 0.1;
  }
  100% {
    transform: translate(-50%, -50%) scale(2);
    opacity: 0;
  }
}

/* Popover styles */
:deep(.n-popover) {
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

:deep(.n-button) {
  border-radius: 8px;
  font-weight: 600;
}
</style>
